import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { useOwnerCompany } from "@/owner/OwnerCompanyContext";
import {
  fetchAllAccountsOverview,
  suspendCompany,
  TIER_LIMITS,
  type AccountOverview,
} from "@/owner/admin-accounts.helpers";
import { EditCompanyDialog } from "@/owner/EditCompanyDialog";
import { DiscountsDialog } from "@/owner/DiscountsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Crown,
  Loader2,
  MoreHorizontal,
  Eye,
  Pencil,
  Ban,
  Trash2,
  Building2,
  Users,
  Wallet,
  Tag,
  AlertCircle,
  CheckCircle2,
  XCircle,
  LifeBuoy,
} from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { deleteCompanyCascade } from "@/owner/admin-accounts.functions";

import { cn } from "@/lib/utils";
import { useLocaleFormat } from "@/lib/i18n-format";

export const Route = createFileRoute("/_app/admin/accounts")({
  component: AdminAccountsPage,
});

const tierColor: Record<AccountOverview["effectiveTier"], string> = {
  enterprise: "bg-purple-100 text-purple-800 border-purple-300",
  business: "bg-blue-100 text-blue-800 border-blue-300",
  pro: "bg-emerald-100 text-emerald-800 border-emerald-300",
  starter: "bg-slate-100 text-slate-800 border-slate-300",
  trial: "bg-amber-100 text-amber-800 border-amber-300",
  none: "bg-red-100 text-red-800 border-red-300",
};

function AdminAccountsPage() {
  const { t } = useTranslation();
  const { formatDate, formatMoney } = useLocaleFormat();
  const { hasRole, loading, setActiveCompany } = useAuth();
  const isOwner = hasRole("owner");
  const navigate = useNavigate();
  const { setSelectedCompanyId } = useOwnerCompany();

  const [accounts, setAccounts] = useState<AccountOverview[]>([]);
  const [fetching, setFetching] = useState(true);
  const [search, setSearch] = useState("");
  const [healthFilter, setHealthFilter] = useState<string>("all");
  const [editing, setEditing] = useState<AccountOverview | null>(null);
  const [suspending, setSuspending] = useState<AccountOverview | null>(null);
  const [deleting, setDeleting] = useState<AccountOverview | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [discountsOpen, setDiscountsOpen] = useState(false);
  const deleteFn = useServerFn(deleteCompanyCascade);

  const load = useCallback(async () => {
    setFetching(true);
    try {
      const data = await fetchAllAccountsOverview();
      setAccounts(data);
    } catch (e) {
      toast.error(t("admin.accounts.loadError"), { description: (e as Error).message });
    } finally {
      setFetching(false);
    }
  }, [t]);

  useEffect(() => {
    if (isOwner) void load();
  }, [isOwner, load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return accounts.filter((a) => {
      if (healthFilter !== "all" && a.health !== healthFilter) return false;
      if (!q) return true;
      return (
        a.companyName.toLowerCase().includes(q) ||
        a.admins.some(
          (ad) => ad.email.toLowerCase().includes(q) || ad.name.toLowerCase().includes(q),
        )
      );
    });
  }, [accounts, search, healthFilter]);

  const stats = useMemo(() => {
    return {
      total: accounts.length,
      working: accounts.filter((a) => a.health === "working").length,
      idle: accounts.filter((a) => a.health === "active_no_use").length,
      noAccess: accounts.filter((a) => a.health === "no_access").length,
      paid: accounts.filter(
        (a) => a.subscription && ["active", "trialing", "past_due"].includes(a.subscription.status),
      ).length,
      totalInvoiced: accounts.reduce((s, a) => s + a.totalInvoiced, 0),
    };
  }, [accounts]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!isOwner) return <Navigate to="/dashboard" />;

  function viewAs(account: AccountOverview) {
    setSelectedCompanyId(account.companyId);
    toast.success(t("admin.accounts.viewAsToast.title", { company: account.companyName }), {
      description: t("admin.accounts.viewAsToast.description"),
    });
    navigate({ to: "/dashboard" });
  }

  async function enterSupport(account: AccountOverview) {
    try {
      await setActiveCompany(account.companyId); // dispara reload no sucesso
    } catch (e) {
      toast.error(t("admin.accounts.enterSupportError"), { description: (e as Error).message });
    }
  }

  async function doSuspend() {
    if (!suspending) return;
    try {
      await suspendCompany(suspending.companyId);
      toast.success(t("admin.accounts.suspend.successToast"));
      setSuspending(null);
      void load();
    } catch (e) {
      toast.error(t("admin.accounts.suspend.errorToast"), { description: (e as Error).message });
    }
  }

  async function doDelete() {
    if (!deleting) return;
    setDeletingBusy(true);
    try {
      await deleteFn({ data: { companyId: deleting.companyId } });
      toast.success(t("admin.accounts.deleteDialog.successToast", { name: deleting.companyName }));
      setDeleting(null);
      setDeleteConfirmText("");
      void load();
    } catch (e) {
      toast.error(t("admin.accounts.deleteDialog.errorToast"), {
        description: (e as Error).message,
      });
    } finally {
      setDeletingBusy(false);
    }
  }

  return (
    <div className="space-y-6 p-4 lg:p-6">
      <div className="flex items-center gap-3">
        <Crown className="h-6 w-6 text-amber-500" />
        <h1 className="text-2xl font-bold">{t("admin.accounts.title")}</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard icon={Building2} label={t("admin.accounts.stats.total")} value={stats.total} />
        <StatCard
          icon={CheckCircle2}
          label={t("admin.accounts.stats.working")}
          value={stats.working}
          accent="text-emerald-600"
        />
        <StatCard
          icon={AlertCircle}
          label={t("admin.accounts.stats.idle")}
          value={stats.idle}
          accent="text-amber-600"
        />
        <StatCard
          icon={XCircle}
          label={t("admin.accounts.stats.noAccess")}
          value={stats.noAccess}
          accent="text-red-600"
        />
        <StatCard
          icon={Wallet}
          label={t("admin.accounts.stats.totalInvoiced")}
          value={formatMoney(stats.totalInvoiced, "USD", { maximumFractionDigits: 0 })}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder={t("admin.accounts.filters.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Select value={healthFilter} onValueChange={setHealthFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.accounts.filters.allStatuses")}</SelectItem>
            <SelectItem value="working">{t("admin.accounts.filters.working")}</SelectItem>
            <SelectItem value="active_no_use">{t("admin.accounts.filters.idle")}</SelectItem>
            <SelectItem value="no_access">{t("admin.accounts.filters.noAccess")}</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} disabled={fetching}>
          {fetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {t("admin.accounts.filters.refresh")}
        </Button>
        {/* Cupons de desconto desativados: o backend (paddle-discounts) dependia do
            Lovable/Paddle, removido na saída do Lovable. Reativar quando/se migrar
            para o Dite Gateway. */}
        <span className="text-sm text-muted-foreground">
          {t("admin.accounts.filters.showingOf", {
            filtered: filtered.length,
            total: accounts.length,
          })}
        </span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("admin.accounts.table.company")}</TableHead>
                  <TableHead>{t("admin.accounts.table.admin")}</TableHead>
                  <TableHead className="text-center">
                    <Users className="inline h-4 w-4" />
                  </TableHead>
                  <TableHead>{t("admin.accounts.table.plan")}</TableHead>
                  <TableHead>{t("admin.accounts.table.subscription")}</TableHead>
                  <TableHead>{t("admin.accounts.table.registered")}</TableHead>
                  <TableHead>{t("admin.accounts.table.trial")}</TableHead>
                  <TableHead className="text-right">{t("admin.accounts.table.usage")}</TableHead>
                  <TableHead>{t("admin.accounts.table.status")}</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fetching && accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="py-12 text-center text-muted-foreground">
                      <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin" />
                      {t("admin.accounts.table.loading")}
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="py-12 text-center text-muted-foreground">
                      {t("admin.accounts.table.empty")}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((a) => (
                    <TableRow key={a.companyId}>
                      <TableCell>
                        <div className="font-medium">{a.companyName}</div>
                      </TableCell>
                      <TableCell>
                        {a.admins.length === 0 ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <div className="space-y-0.5">
                            {a.admins.slice(0, 2).map((ad) => (
                              <div key={ad.id} className="text-xs">
                                <div className="font-medium">{ad.name}</div>
                                <div className="text-muted-foreground">{ad.email}</div>
                              </div>
                            ))}
                            {a.admins.length > 2 && (
                              <div className="text-xs text-muted-foreground">
                                {t("admin.accounts.table.othersCount", {
                                  count: a.admins.length - 2,
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">{a.memberCount}</TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            "rounded border px-2 py-0.5 text-xs font-medium",
                            tierColor[a.effectiveTier],
                          )}
                        >
                          {t(`admin.accounts.tiers.${a.effectiveTier}`)}
                        </span>
                        {a.planOverride && (
                          <div className="mt-0.5 text-[10px] text-purple-600">
                            {t("admin.accounts.table.override", { plan: a.planOverride })}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {a.subscription ? (
                          <div>
                            <div className="text-xs font-medium">{a.subscription.status}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {a.subscription.environment} · {a.subscription.priceId ?? "—"}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs">{formatDate(a.createdAt)}</span>
                      </TableCell>
                      <TableCell>
                        {a.trialEndsAt ? (
                          <span
                            className={cn(
                              "text-xs",
                              new Date(a.trialEndsAt) > new Date()
                                ? "text-emerald-600"
                                : "text-muted-foreground line-through",
                            )}
                          >
                            {formatDate(a.trialEndsAt)}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        <UsageCell account={a} />
                        <div className="text-muted-foreground">
                          {formatMoney(a.totalInvoiced, "USD", { maximumFractionDigits: 0 })}
                        </div>
                        {a.lastActivityAt && (
                          <div className="text-[10px] text-muted-foreground">
                            {t("admin.accounts.table.lastActivity", {
                              date: formatDate(a.lastActivityAt),
                            })}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <HealthBadge health={a.health} />
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => enterSupport(a)}>
                              <LifeBuoy className="mr-2 h-4 w-4" />
                              {t("admin.accounts.actions.enterSupport")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => viewAs(a)}>
                              <Eye className="mr-2 h-4 w-4" />
                              {t("admin.accounts.actions.viewAs")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditing(a)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              {t("admin.accounts.actions.edit")}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => setSuspending(a)}
                            >
                              <Ban className="mr-2 h-4 w-4" />
                              {t("admin.accounts.actions.suspend")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-700"
                              onClick={() => {
                                setDeleteConfirmText("");
                                setDeleting(a);
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t("admin.accounts.actions.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <EditCompanyDialog account={editing} onClose={() => setEditing(null)} onSaved={load} />

      <DiscountsDialog open={discountsOpen} onClose={() => setDiscountsOpen(false)} />

      <AlertDialog open={!!suspending} onOpenChange={(o) => !o && setSuspending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.accounts.suspend.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              <Trans
                i18nKey="admin.accounts.suspend.description"
                values={{ company: suspending?.companyName ?? "" }}
                components={[<strong key="0" />]}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("admin.accounts.suspend.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doSuspend} className="bg-red-600 hover:bg-red-700">
              {t("admin.accounts.suspend.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleting}
        onOpenChange={(o) => {
          if (!o && deletingBusy) return;
          if (!o) setDeleting(null);
          setDeleteConfirmText("");
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.accounts.deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              <Trans
                i18nKey="admin.accounts.deleteDialog.description"
                values={{ name: deleting?.companyName ?? "" }}
                components={[<strong key="0" />]}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="delete-confirm-input">
              {t("admin.accounts.deleteDialog.confirmPrompt")}
            </label>
            <Input
              id="delete-confirm-input"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={t("admin.accounts.deleteDialog.confirmPlaceholder")}
              disabled={deletingBusy}
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingBusy}>
              {t("admin.accounts.deleteDialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void doDelete();
              }}
              disabled={
                deletingBusy ||
                deleteConfirmText.trim().toLowerCase() !==
                  (deleting?.companyName ?? "").trim().toLowerCase()
              }
              className="bg-red-700 hover:bg-red-800"
            >
              {deletingBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("admin.accounts.deleteDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Building2;
  label: string;
  value: string | number;
  accent?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Icon className={cn("h-4 w-4", accent)} />
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn("text-2xl font-bold", accent)}>{value}</div>
      </CardContent>
    </Card>
  );
}

function HealthBadge({ health }: { health: AccountOverview["health"] }) {
  const { t } = useTranslation();
  const map = {
    working: { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: CheckCircle2 },
    active_no_use: { cls: "bg-amber-100 text-amber-800 border-amber-300", Icon: AlertCircle },
    no_access: { cls: "bg-red-100 text-red-800 border-red-300", Icon: XCircle },
  } as const;
  const { cls, Icon } = map[health];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium",
        cls,
      )}
    >
      <Icon className="h-3 w-3" />
      {t(`admin.accounts.health.${health}`)}
    </span>
  );
}

function UsageCell({ account }: { account: AccountOverview }) {
  const { t } = useTranslation();
  const tierKey = account.effectiveTier === "none" ? null : account.effectiveTier;
  const limits =
    tierKey && tierKey in TIER_LIMITS ? TIER_LIMITS[tierKey as keyof typeof TIER_LIMITS] : null;
  const fmt = (used: number, lim: number | undefined) => {
    if (lim === undefined || lim === null) return `${used}`;
    if (lim === -1) return `${used}/∞`;
    return `${used}/${lim}`;
  };
  const over = (used: number, lim: number | undefined) =>
    lim !== undefined && lim !== -1 && used > lim;
  return (
    <div className="space-y-0.5">
      <div className="flex justify-end gap-2 font-mono text-[11px]">
        <span
          title={t("admin.accounts.usage.clients")}
          className={cn(over(account.clientCount, limits?.clients) && "text-red-600 font-semibold")}
        >
          {fmt(account.clientCount, limits?.clients)}c
        </span>
        <span
          title={t("admin.accounts.usage.employees")}
          className={cn(
            over(account.memberCount, limits?.employees) && "text-red-600 font-semibold",
          )}
        >
          {fmt(account.memberCount, limits?.employees)}f
        </span>
        <span
          title={t("admin.accounts.usage.jobSites")}
          className={cn(
            over(account.jobSiteCount, limits?.jobSites) && "text-red-600 font-semibold",
          )}
        >
          {fmt(account.jobSiteCount, limits?.jobSites)}o
        </span>
      </div>
      <div className="text-[10px] text-muted-foreground">
        {account.estimateCount}e · {account.invoiceCount}i
      </div>
    </div>
  );
}
